import { ref } from 'vue'
import _ from 'lodash'
import IntegrationService from '@/services/IntegrationService'
import utils from '@/utils'
import { showModal } from '@/Utils/modalBus'
import { useNotification } from '@kyvg/vue3-notification'

export function usePlatformMetadata({ streamPlatforms }) {
	const { notify } = useNotification()
	const configurablePlatform = ref({})
	const configurableField = ref(null)

	function hasMetaProp(propname, platform) {
		if (!propname || !platform) return
		return propname in (platform.serviceMetaEditor?.form || {})
	}

	function metaChangedProps(platform) {
		const changes = platform?.serviceMetaEditor?.changes || {}
		const form = platform?.serviceMetaEditor?.form || {}
		return ['title', 'description', 'game'].filter((p) => p in form && changes[p])
	}

	function onMetaPropChange(propname, platform) {
		if (!propname || !platform) return
		const hasChanged =
			platform.serviceMetaEditor.form[propname] !== platform.serviceMeta[propname]
		platform.serviceMetaEditor.changes[propname] = hasChanged
		replacePlatform(platform)
	}

	// The list can be re-normalized (new row objects) while a save is in flight, so always go by _id
	function replacePlatform(platform) {
		const pindex = _.findIndex(streamPlatforms.value, { _id: platform._id })
		if (pindex === -1) return
		streamPlatforms.value = utils.updateArrayItem(streamPlatforms.value, platform, pindex)
	}

	async function saveMetaProp(propname, platform) {
		if (!propname || !platform) return
		const newValue = platform.serviceMetaEditor.form[propname]
		platform.serviceMetaEditor.formProcessing[propname] = true
		replacePlatform(platform)

		let saved = true
		try {
			await IntegrationService.updateIntegrationMetadata(
				platform.linkedServiceCreds,
				platform.serviceMetaEditor.form
			)
		} catch (e) {
			saved = false
			notify({
				group: 'error',
				text: 'Unable to update the stream metadata.',
			})
		}

		const current = _.find(streamPlatforms.value, { _id: platform._id }) || platform
		current.serviceMetaEditor.formProcessing[propname] = false
		if (saved) {
			current.serviceMeta = _.assign({}, current.serviceMeta, {
				[propname]: newValue,
			})
			current.serviceMetaEditor.changes[propname] = false
		}
		replacePlatform(current)
	}

	async function saveAllMeta(platform) {
		for (const prop of metaChangedProps(platform)) {
			await saveMetaProp(prop, platform)
		}
	}

	function requestMetaToAllPlatforms(platform) {
		configurablePlatform.value = platform
		configurableField.value = null
		showModal('all-platforms-update-confirm')
	}

	async function onAllPlatformsUpdateConfirm(propname, platform) {
		const propsToApply = propname ? [propname] : metaChangedProps(platform)
		propsToApply.forEach((prop) => {
			const newValue = platform.serviceMetaEditor.form[prop]
			streamPlatforms.value.forEach((item) => {
				if (['facebook live', 'youtube events', 'twitch'].indexOf(item.template) === -1) return
				if (item.template === 'twitch' && prop === 'description') return
				if (item.serviceMetaEditor.form && item.serviceMetaEditor.form[prop]) {
					item.serviceMetaEditor.form[prop] = newValue
					saveMetaProp(prop, item)
				}
			})
		})
	}

	const onChangeGameName = _.debounce(async (eventOrName, platform) => {
		const name = typeof eventOrName === 'string' ? eventOrName : eventOrName?.target?.value
		if (!platform.linkedServiceCreds || !name) return
		await IntegrationService.getTwitchCategories(platform.linkedServiceCreds, name)
	}, 250)

	return {
		configurablePlatform,
		configurableField,
		hasMetaProp,
		onMetaPropChange,
		saveAllMeta,
		saveMetaProp,
		requestMetaToAllPlatforms,
		onAllPlatformsUpdateConfirm,
		onChangeGameName,
	}
}
